import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

export type SiteReview = {
  id: string;
  author: string;
  city: string;
  event_type: string;
  rating: number;
  date: string | null;
  text: string;
  venue: string | null;
  guests: number | null;
  image_url: string | null;
  source: string | null;
  ordre: number;
  actif: boolean;
  valide: boolean;
};

const reviewInput = z.object({
  author: z.string().trim().min(1).max(120),
  city: z.string().trim().max(120).default(""),
  event_type: z.string().trim().max(60).default("Google"),
  rating: z.number().int().min(1).max(5).default(5),
  date: z.string().trim().max(20).nullable().optional(),
  text: z.string().trim().min(1).max(4000),
});

// ── Lecture publique ─────────────────────────────────────────────────────────
// Priorite : 1) Featurable (tous les avis Google, gratuit) 2) table site_reviews
export const getPublicReviews = createServerFn({ method: "GET" }).handler(async () => {
  // 1. API gratuite Featurable — renvoie l'integralite des avis Google
  const { fetchFeaturableReviews } = await import("./featurable.server");
  const featurable = await fetchFeaturableReviews();
  if (featurable) {
    return {
      source: "featurable" as const,
      total: featurable.total,
      average: featurable.average,
      rows: featurable.rows.map((r, i) => ({
        id: r.id,
        author: r.author,
        city: "",
        event_type: "Google",
        rating: r.rating,
        date: r.date,
        text: r.text,
        venue: null,
        guests: null,
        image_url: null,
        source: "google",
        ordre: i,
        actif: true,
        valide: true,
      })) as SiteReview[],
    };
  }

  // 2. Avis importes manuellement dans le back-office
  const { createClient } = await import("@supabase/supabase-js");
  const sb = createClient(
    process.env["SUPABASE_URL"]!,
    process.env["SUPABASE_PUBLISHABLE_KEY"]!,
    { auth: { persistSession: false, autoRefreshToken: false } },
  );
  const { data, error } = await sb
    .from("site_reviews")
    .select("id,author,city,event_type,rating,date,text,venue,guests,image_url,source,ordre,actif,valide")
    .eq("actif", true)
    .eq("valide", true)
    .order("date", { ascending: false, nullsFirst: false })
    .order("ordre", { ascending: true });
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as SiteReview[];
  return {
    source: "database" as const,
    total: rows.length,
    average: 5,
    rows,
  };
});


// ── Admin ────────────────────────────────────────────────────────────────────
export const adminListReviews = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => z.object({ password: z.string().min(1) }).parse(d))
  .handler(async ({ data }) => {
    const { getAdminPasswordStatus } = await import("./admin-auth.server");
    const status = getAdminPasswordStatus(data.password);
    if (status === "missing") throw new Error("Admin password not configured");
    if (status === "invalid") return { authError: true as const, rows: [] };
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: rows, error } = await supabaseAdmin
      .from("site_reviews")
      .select("*")
      .order("ordre", { ascending: true })
      .order("date", { ascending: false });
    if (error) throw new Error(error.message);
    return { rows: (rows ?? []) as SiteReview[] };
  });

export const adminImportReviews = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z
      .object({
        password: z.string().min(1),
        replaceAll: z.boolean().default(true),
        reviews: z.array(reviewInput).min(1).max(200),
      })
      .parse(d),
  )
  .handler(async ({ data }) => {
    const { getAdminPasswordStatus } = await import("./admin-auth.server");
    const status = getAdminPasswordStatus(data.password);
    if (status === "missing") throw new Error("Admin password not configured");
    if (status === "invalid") return { authError: true as const, inserted: 0 };
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    if (data.replaceAll) {
      const { error } = await supabaseAdmin
        .from("site_reviews")
        .delete()
        .neq("id", "00000000-0000-0000-0000-000000000000");
      if (error) throw new Error(error.message);
    }

    const rows = data.reviews.map((r, i) => ({
      author: r.author,
      city: r.city ?? "",
      event_type: r.event_type ?? "Google",
      rating: r.rating,
      date: r.date || null,
      text: r.text,
      source: "google",
      ordre: i,
      actif: true,
      valide: true,
    }));

    const { error: insErr } = await supabaseAdmin.from("site_reviews").insert(rows);
    if (insErr) throw new Error(insErr.message);
    return { inserted: rows.length };
  });

export const adminUpdateReview = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z
      .object({
        password: z.string().min(1),
        id: z.string().uuid(),
        patch: z.object({
          author: z.string().trim().min(1).max(120).optional(),
          text: z.string().trim().min(1).max(4000).optional(),
          date: z.string().trim().max(20).nullable().optional(),
          rating: z.number().int().min(1).max(5).optional(),
          city: z.string().trim().max(120).optional(),
          event_type: z.string().trim().max(60).optional(),
          ordre: z.number().int().min(0).max(999).optional(),
          actif: z.boolean().optional(),
          valide: z.boolean().optional(),
        }),
      })
      .parse(d),
  )
  .handler(async ({ data }) => {
    const { getAdminPasswordStatus } = await import("./admin-auth.server");
    const status = getAdminPasswordStatus(data.password);
    if (status === "missing") throw new Error("Admin password not configured");
    if (status === "invalid") return { authError: true as const };
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin
      .from("site_reviews")
      .update({ ...data.patch, updated_at: new Date().toISOString() })
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true as const };
  });

export const adminDeleteReview = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z.object({ password: z.string().min(1), id: z.string().uuid() }).parse(d),
  )
  .handler(async ({ data }) => {
    const { getAdminPasswordStatus } = await import("./admin-auth.server");
    const status = getAdminPasswordStatus(data.password);
    if (status === "missing") throw new Error("Admin password not configured");
    if (status === "invalid") return { authError: true as const };
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin.from("site_reviews").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true as const };
  });
